import { doc, updateDoc, getDoc, collection, addDoc, increment, getDocs } from 'firebase/firestore';
import { db } from '../App';

const roundToTwoDecimals = (number) => {
  return parseFloat(number.toFixed(2));
};

export const fetchBitcoinPrice = async () => {
  const response = await fetch('https://api.coindesk.com/v1/bpi/currentprice/BTC.json');
  const data = await response.json();
  return roundToTwoDecimals(data.bpi.USD.rate_float);
};

export const fetchStockPrices = async () => {
  const stocksCollection = collection(db, 'stocks');
  const stocksSnapshot = await getDocs(stocksCollection);
  const stocksData = stocksSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  return stocksData;
};

export const buyCrypto = async (userId, amount, price) => {
  const userRef = doc(db, 'users', userId);
  const investmentsRef = collection(userRef, 'investments');
  const userDoc = await getDoc(userRef);

  if (!userDoc.exists()) throw new Error('User not found');

  const userData = userDoc.data();
  const cost = roundToTwoDecimals(amount * price);
  if (userData.money < cost) throw new Error('Insufficient funds');

  const investment = {
    ttype: 'hahumCoin',
    amount: roundToTwoDecimals(amount),
    price: roundToTwoDecimals(price),
    spent: cost,
    timestamp: new Date()
  };

  await addDoc(investmentsRef, investment);
  await updateDoc(userRef, {
    money: roundToTwoDecimals(userData.money - cost),
    'crypto.hahumCoin': increment(roundToTwoDecimals(amount))
  });
};

export const fetchUserInvestments = async (userId) => {
  const userRef = doc(db, 'users', userId);
  const investmentsRef = collection(userRef, 'investments');
  const investmentsSnapshot = await getDocs(investmentsRef);
  const investmentsData = investmentsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  return investmentsData;
};

export const sellCrypto = async (userId, amount, price) => {
  const userRef = doc(db, 'users', userId);
  const investmentsRef = collection(userRef, 'investments');
  const userDoc = await getDoc(userRef);

  if (!userDoc.exists()) throw new Error('User not found');

  const userData = userDoc.data();
  const totalHC = userData.crypto?.hahumCoin || 0;

  if (totalHC < amount) throw new Error('Insufficient Hahum Coin');

  const earnings = roundToTwoDecimals(amount * price);

  const investment = {
    ttype: 'hahumCoin',
    amount: roundToTwoDecimals(-amount),
    price: roundToTwoDecimals(price),
    earned: earnings,
    timestamp: new Date()
  };

  await addDoc(investmentsRef, investment);
  await updateDoc(userRef, {
    money: roundToTwoDecimals(userData.money + earnings),
    'crypto.hahumCoin': increment(roundToTwoDecimals(-amount))
  });
};

export const buyStock = async (userId, stockSymbol, amount, price) => {
  const userRef = doc(db, 'users', userId);
  const investmentsRef = collection(userRef, 'investments');
  const userDoc = await getDoc(userRef);

  if (!userDoc.exists()) throw new Error('User not found');

  const userData = userDoc.data();
  const cost = roundToTwoDecimals(amount * price);
  if (userData.money < cost) throw new Error('Insufficient funds');

  const investment = {
    ttype: stockSymbol,
    amount: roundToTwoDecimals(amount),
    price: roundToTwoDecimals(price),
    spent: cost,
    timestamp: new Date()
  };

  const existingStockIndex = (userData.stocks || []).findIndex(stock => stock.symbol === stockSymbol);
  const updatedStocks = [...(userData.stocks || [])];

  if (existingStockIndex >= 0) {
    updatedStocks[existingStockIndex].amount = roundToTwoDecimals(updatedStocks[existingStockIndex].amount + amount);
  } else {
    updatedStocks.push({ symbol: stockSymbol, amount: roundToTwoDecimals(amount) });
  }

  await addDoc(investmentsRef, investment);
  await updateDoc(userRef, {
    money: roundToTwoDecimals(userData.money - cost),
    stocks: updatedStocks
  });
};

export const sellStock = async (userId, stockSymbol, amount, price) => {
  const userRef = doc(db, 'users', userId);
  const investmentsRef = collection(userRef, 'investments');
  const userDoc = await getDoc(userRef);

  if (!userDoc.exists()) throw new Error('User not found');

  const userData = userDoc.data();
  const existingStockIndex = (userData.stocks || []).findIndex(stock => stock.symbol === stockSymbol);

  if (existingStockIndex === -1 || userData.stocks[existingStockIndex].amount < amount) {
    throw new Error('Insufficient stock amount');
  }

  const earnings = roundToTwoDecimals(amount * price);

  const investment = {
    ttype: stockSymbol,
    amount: roundToTwoDecimals(-amount),
    price: roundToTwoDecimals(price),
    earned: earnings,
    timestamp: new Date()
  };

  const updatedStocks = [...(userData.stocks || [])];
  updatedStocks[existingStockIndex].amount = roundToTwoDecimals(updatedStocks[existingStockIndex].amount - amount);

  if (updatedStocks[existingStockIndex].amount === 0) {
    updatedStocks.splice(existingStockIndex, 1);
  }

  await addDoc(investmentsRef, investment);
  await updateDoc(userRef, {
    money: roundToTwoDecimals(userData.money + earnings),
    stocks: updatedStocks
  });
};

export const fetchUserHoldings = async (userId) => {
  const userRef = doc(db, 'users', userId);
  const userDoc = await getDoc(userRef);

  if (!userDoc.exists()) throw new Error('User not found');

  const userData = userDoc.data();
  return userData;
};
